
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { jwtDecode } from 'jwt-decode'; // ✅ Named import for v4+

interface LoginResponse {
  message?: string;
  token?: string;
  jwtDuration?: number;     // optional
  totalDuration?: number;   // optional
}

interface JwtPayload {
  exp: number; // expiration time in seconds since epoch
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
private router = inject(Router);

  private readonly baseUrl: string = environment.authApiBase;
  private readonly TOKEN_KEY: string = 'jwtToken'; // ✅ FIXED

  /** Attempt login and store JWT token on success */
  login(username: string, password: string): Observable<boolean> {
    const body = { username, password };
    const url = `${this.baseUrl}/login`;

    console.log('[AuthService] Sending login request to:', url, 'with body:', body);

    return this.http.post<LoginResponse>(url, body).pipe(
      tap(response => {
        console.log('[AuthService] Login response:', response);
        if (response?.token) {
          localStorage.setItem(this.TOKEN_KEY, response.token);
        }
      }),
      map(response => !!response?.token),
      catchError(error => {
        console.error('[AuthService] Login error:', error);
        return of(false);
      })
    );
  }

  /** Clear stored token and navigate to home */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.router.navigate(['/']);
    console.log('[AuthService] Logged out and navigated to home');
  }

  /** Check if token exists and is not expired using JWT exp claim */
  isLoggedIn(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) return false;

    try {
      const payload: JwtPayload = jwtDecode<JwtPayload>(token);
      const now = Math.floor(Date.now() / 1000); // current time in seconds
      if (payload.exp < now) {
        console.warn('[AuthService] Token expired, performing logout');
        this.logout();
        return false;
      }
      return true;
    } catch (e) {
      console.error('[AuthService] Invalid token format:', e);
      this.logout();
      return false;
    }
  }

  /** Get raw token for interceptors */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }
}
